// Widget-owned modal dialog, used by bar mode's "Explore My Options"
// handoff (see index.js): once the bar decodes a VIN/plate it opens a
// second, full-size widget instance in this overlay, already on the
// vehicle-confirm screen.
//
// This deliberately lives *inside the widget bundle* rather than in the
// host page's own helper script/stylesheet (public/widget-helpers.js/.css,
// which is only a reference implementation dealers may or may not copy
// correctly). A live dealer site that loaded the helper JS but not its CSS
// got an overlay with no `position: fixed` and no `display: none` — so the
// continuation flow rendered as an ordinary block at the very end of the
// page and the user was scrolled to the bottom of the dealer's site to
// reach it, instead of getting a dialog. Nothing here depends on host-page
// CSS: the overlay's own layout is set as inline `!important` styles, and
// everything inside it lives in a shadow root of its own.
import { getPartnerThemeVars } from "./applyTheme";

const OVERLAY_ATTR = "data-hrx-widget-modal";

const OVERLAY_STYLES = {
  display: "block",
  position: "fixed",
  top: "0",
  right: "0",
  bottom: "0",
  left: "0",
  width: "auto",
  height: "auto",
  margin: "0",
  padding: "0",
  border: "0",
  // Above effectively any host-page chrome (sticky headers, chat widgets).
  "z-index": "2147483000",
};

const PANEL_CSS = `
:host { all: initial; }

.hrx-modal-backdrop {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  overflow-y: auto;
  background: rgba(15, 23, 42, 0.6);
  font-family: system-ui, -apple-system, "Segoe UI", sans-serif;
}

.hrx-modal-panel {
  position: relative;
  width: 100%;
  max-width: 960px;
  max-height: 90vh;
  overflow-y: auto;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.35);
  animation: hrx-modal-pop 0.25s ease-out;
  outline: none;
}

.hrx-modal-close {
  position: absolute;
  top: 6px;
  right: 10px;
  /* Beats the widget's own internal sticky header (z-index: 50), which
     renders in the nested shadow root on the mount div below. */
  z-index: 100;
  padding: 4px 8px;
  border: none;
  background: none;
  font-size: 28px;
  line-height: 1;
  color: #64748b;
  cursor: pointer;
}

.hrx-modal-close:hover {
  color: #0f172a;
}

@keyframes hrx-modal-pop {
  from { transform: scale(0.92); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
}

@media (max-width: 640px) {
  .hrx-modal-backdrop { padding: 8px; }
  .hrx-modal-panel { max-height: 96vh; }
}
`;

// Only one widget modal is open at a time; opening again (eg. the user
// closed it and looked up a second vehicle in the bar) tears the previous
// one down so the new instance mounts fresh with the new vehicle rather
// than reusing a mount still seeded with the old one.
let current = null;

export function closeWidgetModal() {
  if (!current) return;
  const { host, root, onKeyDown, prevBodyOverflow } = current;
  current = null;

  document.removeEventListener("keydown", onKeyDown);
  document.body.style.overflow = prevBodyOverflow;
  root?.unmount();
  host.remove();
}

/**
 * Opens the widget in a modal dialog appended to document.body.
 *
 * @param {object}   opts
 * @param {Function} opts.mount          index.js's mountApp — passed in rather
 *                                       than imported, to keep this module free
 *                                       of a circular dependency on the entry
 *                                       point. Must return the React root.
 * @param {string}   [opts.partnerKey]   partner branding key to carry over.
 * @param {object}   [opts.initialVehicle] decoded vehicle to seed the mount with,
 *                                       so it lands on /confirm-vehicle.
 */
export function openWidgetModal({ mount, partnerKey, initialVehicle = null }) {
  closeWidgetModal();

  const host = document.createElement("div");
  host.setAttribute(OVERLAY_ATTR, "");
  if (partnerKey) host.dataset.partner = partnerKey;
  // Inline + !important: a host page rule like `body div { position: static }`
  // must not be able to knock the overlay out of the viewport and back into
  // the page flow.
  Object.entries(OVERLAY_STYLES).forEach(([prop, value]) => {
    host.style.setProperty(prop, value, "important");
  });

  const shadow = host.attachShadow({ mode: "open" });

  const style = document.createElement("style");
  style.textContent = PANEL_CSS;

  const backdrop = document.createElement("div");
  backdrop.className = "hrx-modal-backdrop";

  const panel = document.createElement("div");
  panel.className = "hrx-modal-panel";
  panel.setAttribute("role", "dialog");
  panel.setAttribute("aria-modal", "true");
  panel.setAttribute("aria-label", "Value your trade");
  panel.tabIndex = -1;

  const closeBtn = document.createElement("button");
  closeBtn.type = "button";
  closeBtn.className = "hrx-modal-close";
  closeBtn.setAttribute("aria-label", "Close");
  closeBtn.innerHTML = "&times;";
  closeBtn.addEventListener("click", closeWidgetModal);

  // The widget mounts here and attaches its own (nested) shadow root to
  // this div, exactly as it does for any other embed target. The partner
  // theme has to be set on it inline: applyTheme.js's document-level style
  // tag can't select into this overlay's shadow tree, and merely inheriting
  // the properties from above loses to index.css's own `:host` defaults,
  // which land on this very element — the modal would render in default
  // Nucar colors while the bar that opened it was partner-branded.
  const mountTarget = document.createElement("div");
  Object.entries(getPartnerThemeVars()).forEach(([name, value]) => {
    mountTarget.style.setProperty(name, value);
  });

  panel.appendChild(closeBtn);
  panel.appendChild(mountTarget);
  backdrop.appendChild(panel);
  shadow.appendChild(style);
  shadow.appendChild(backdrop);

  // Clicks inside the shadow tree are retargeted to the host, so the
  // dismiss-on-backdrop check has to live in here where the real target is
  // still visible.
  backdrop.addEventListener("click", (e) => {
    if (e.target === backdrop) closeWidgetModal();
  });

  const onKeyDown = (e) => {
    if (e.key === "Escape") closeWidgetModal();
  };
  document.addEventListener("keydown", onKeyDown);

  const prevBodyOverflow = document.body.style.overflow;
  // Locking page scroll also keeps the widget's own per-screen
  // scrollIntoView (App.js) confined to the panel — it can't drag the host
  // page around underneath the dialog.
  document.body.style.overflow = "hidden";

  document.body.appendChild(host);

  // Partner branding is already resolved and applied globally by the mount
  // that opened this modal (brandConfig singleton + applyTheme's style tag,
  // which reaches in here via WIDGET_ROOT_ATTR above), so only the vehicle
  // needs handing over.
  const root = mount(mountTarget, { initialVehicle });
  current = { host, root, onKeyDown, prevBodyOverflow };

  // preventScroll: the host page must not jump when focus moves into the
  // dialog — the whole point of this being a dialog.
  panel.focus({ preventScroll: true });

  return closeWidgetModal;
}
